import React, { useState, useEffect } from 'react';

const styles = `
  @keyframes fp-spin-1 { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes fp-spin-2 { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes fp-spin-3 { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes fp-spin-4 { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes fp-glow   { 0%,100%{opacity:0.4} 50%{opacity:0.9} }
  @keyframes fadeUp    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes msgFade   { 0%{opacity:0;transform:translateY(8px)} 20%,80%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(-8px)} }
  @keyframes dotPulse  { 0%,100%{opacity:0.3} 50%{opacity:1} }

  * { box-sizing:border-box; margin:0; padding:0; }

  .gen-root {
    min-height:100vh;
    background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(26,106,255,0.12) 0%, transparent 60%), #050C1A;
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    padding:40px 24px; position:relative; overflow:hidden;
    font-family:'Inter',sans-serif;
  }
  .gen-container { position:relative; z-index:1; display:flex; flex-direction:column; align-items:center; animation:fadeUp 0.6s ease forwards; }
  .gen-fp { position:relative; width:120px; height:120px; display:flex; align-items:center; justify-content:center; margin-bottom:48px; }
  .gen-fp-ring { position:absolute; border-radius:50%; border:1px solid transparent; top:50%; left:50%; }
  .gen-fp-ring-1 { width:120px; height:120px; margin:-60px 0 0 -60px; border-color:rgba(26,106,255,0.3); border-top-color:#1A6AFF; animation:fp-spin-1 12s linear infinite; }
  .gen-fp-ring-2 { width:96px;  height:96px;  margin:-48px 0 0 -48px; border-color:rgba(6,182,212,0.2);  border-right-color:#06B6D4; animation:fp-spin-2 8s linear infinite; }
  .gen-fp-ring-3 { width:72px;  height:72px;  margin:-36px 0 0 -36px; border-color:rgba(26,106,255,0.12); border-bottom-color:rgba(26,106,255,0.55); animation:fp-spin-3 6s linear infinite; }
  .gen-fp-ring-4 { width:48px;  height:48px;  margin:-24px 0 0 -24px; border-color:rgba(6,182,212,0.08);  border-left-color:rgba(6,182,212,0.35); animation:fp-spin-4 4s linear infinite; }
  .gen-fp-core { width:14px; height:14px; border-radius:50%; background:#1A6AFF; animation:fp-glow 2s ease-in-out infinite; position:relative; z-index:1; }
  .gen-wordmark { font-family:'Space Grotesk',sans-serif; font-size:11px; font-weight:600; letter-spacing:0.2em; text-transform:uppercase; color:rgba(237,241,255,0.22); margin-bottom:36px; }
  .gen-name-pill { font-family:'Space Grotesk',sans-serif; font-size:18px; font-weight:700; letter-spacing:-0.02em; color:#06B6D4; margin-bottom:20px; }
  .gen-concierge { font-family:'Inter',sans-serif; font-size:17px; font-weight:300; color:rgba(237,241,255,0.72); text-align:center; line-height:1.65; min-height:56px; max-width:460px; animation:msgFade 4s ease forwards; letter-spacing:-0.01em; }
  .gen-phase-label { margin-top:44px; font-size:10px; font-weight:500; letter-spacing:0.15em; text-transform:uppercase; color:rgba(237,241,255,0.2); margin-bottom:10px; }
  .gen-progress-track { width:280px; height:1px; background:rgba(255,255,255,0.06); border-radius:1px; overflow:hidden; }
  .gen-progress-fill { height:100%; background:linear-gradient(90deg,#1A6AFF,#06B6D4); border-radius:1px; transition:width 0.8s ease; }
  .gen-phases { margin-top:36px; display:flex; gap:8px; align-items:center; }
  .gen-phase-dot { width:5px; height:5px; border-radius:50%; background:rgba(255,255,255,0.1); transition:all 0.4s ease; }
  .gen-phase-dot.active { background:#1A6AFF; box-shadow:0 0 8px rgba(26,106,255,0.6); animation:dotPulse 1.5s ease-in-out infinite; }
  .gen-phase-dot.done   { background:#06B6D4; }
  .gen-brief { margin-top:44px; max-width:400px; text-align:center; font-size:13px; color:rgba(237,241,255,0.2); line-height:1.6; font-style:italic; border-top:1px solid rgba(255,255,255,0.05); padding-top:22px; }
`;

const NAME_MESSAGES = [
  "Searching the naming universe for your brand.",
  "Evaluating linguistic power and cultural resonance.",
  "Testing trademark viability across key markets.",
  "Mapping domain availability and digital presence.",
  "Curating your five strongest candidates.",
];

const BRAND_MESSAGES = [
  "Building your brand DNA from the ground up.",
  "Defining the truth at the heart of your brand.",
  "Constructing your visual architecture.",
  "Crafting the words that make your market stop.",
  "Mapping your position in the competitive landscape.",
  "Designing every moment your customer feels you.",
  "Building your digital presence strategy.",
  "Preparing your go-to-market plan.",
  "Engineering your launch and activation sequence.",
  "Your brand system is almost complete.",
];

const PHASES = [
  'Nomenclature','Brand DNA','Company Identity','Visual Architecture',
  'Brand Messaging','Strategic Positioning','Customer Experience',
  'Digital Presence','Go-To-Market','Launch',
];

export default function Generation({ brief, mode = 'names', confirmedName }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);

  const messages = mode === 'brand' ? BRAND_MESSAGES : NAME_MESSAGES;

  useEffect(() => {
    const msgTimer = setInterval(() => setMsgIndex(i => (i + 1) % messages.length), 4000);

    // Fix: Progress bar now slows down asymptotically towards 98% instead of stopping dead.
    const progressTimer = setInterval(() => {
      setProgress(p => {
        const remaining = 98 - p;
        const step = Math.max(0.1, remaining * 0.08 * Math.random());
        return Math.min(p + step, 98);
      });
    }, 800);

    const phaseTimer = setInterval(() => setCurrentPhase(p => Math.min(p + 1, PHASES.length - 1)), 5500);
    return () => { clearInterval(msgTimer); clearInterval(progressTimer); clearInterval(phaseTimer); };
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="gen-root" role="alert" aria-busy="true" aria-label={mode === 'brand' ? 'Building Brand' : 'Generating Names'}>
        <div className="gen-container">
          <div className="gen-fp" aria-hidden="true">
            <div className="gen-fp-ring gen-fp-ring-1" />
            <div className="gen-fp-ring gen-fp-ring-2" />
            <div className="gen-fp-ring gen-fp-ring-3" />
            <div className="gen-fp-ring gen-fp-ring-4" />
            <div className="gen-fp-core" />
          </div>

          <div className="gen-wordmark">
            {mode === 'brand' ? 'Brandostro — Building Your Brand' : 'Brandostro — Naming Engine'}
          </div>

          {mode === 'brand' && confirmedName && (
            <div className="gen-name-pill">{confirmedName}</div>
          )}

          <div key={msgIndex} className="gen-concierge" aria-live="polite">{messages[msgIndex]}</div>

          <div className="gen-phase-label">
            Phase {String(currentPhase).padStart(2,'0')} · {PHASES[currentPhase]}
          </div>

          <div className="gen-progress-track" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
            <div className="gen-progress-fill" style={{ width:`${progress}%` }} />
          </div>

          <div className="gen-phases" aria-hidden="true">
            {PHASES.map((_,i) => (
              <div key={i} className={`gen-phase-dot ${i===currentPhase?'active':i<currentPhase?'done':''}`} />
            ))}
          </div>

          {brief && (
            <div className="gen-brief">
              "{brief.length > 120 ? brief.substring(0,120)+'...' : brief}"
            </div>
          )}
        </div>
      </div>
    </>
  );
}